import React, { useRef, useEffect, useState } from 'react';
import './AudioPlayer.css';

function AudioPlayer() {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(true); // Always show "Music On" by default
  const hasAttemptedPlayRef = useRef(false);
  const userInteractedRef = useRef(false);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Set audio properties
    audio.volume = 0.5; // Set volume to 50% (adjustable)
    audio.loop = true; // Ensure looping is enabled
    
    // Function to attempt playing audio
    const attemptPlay = async () => {
      if (hasAttemptedPlayRef.current) return;
      hasAttemptedPlayRef.current = true;
      
      try {
        await audio.play();
        setIsPlaying(true);
      } catch (error) {
        // Auto-play was prevented, will play on first user interaction
        // Keep state as true (Music On) even if autoplay fails
        setIsPlaying(true);
      }
    };

    // Try to play immediately
    attemptPlay();

    // If autoplay fails, start playing on first user interaction anywhere on page
    const handleFirstInteraction = async () => {
      if (!userInteractedRef.current && audio.paused) {
        userInteractedRef.current = true;
        try {
          await audio.play();
          setIsPlaying(true);
        } catch (error) {
          console.log('Failed to play on interaction:', error);
        }
      }
    };

    // Listen for user interactions to start playback (only if audio is paused)
    const events = ['click', 'touchstart', 'keydown', 'scroll'];
    events.forEach(event => {
      document.addEventListener(event, handleFirstInteraction, { once: true, passive: true });
    });

    // Update state when audio plays/pauses
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => {
      // Restart if it somehow ends (shouldn't happen with loop)
      if (audioRef.current && isPlaying) {
        audioRef.current.play();
      }
    };

    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      events.forEach(event => {
        document.removeEventListener(event, handleFirstInteraction);
      });
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []); // Remove isPlaying from dependencies

  const handlePlayPause = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (audioRef.current) {
      userInteractedRef.current = true; // Mark that user has interacted
      
      if (audioRef.current.paused) {
        try {
          await audioRef.current.play();
          setIsPlaying(true);
        } catch (error) {
          console.log('Failed to play:', error);
          setIsPlaying(false);
        }
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  return (
    <div className="audio-player-container">
      <audio
        ref={audioRef}
        loop
        preload="auto"
        autoPlay
      >
        <source src="/audio/om-namo-venkatesaya.mp3" type="audio/mpeg" />
        Your browser does not support the audio element.
      </audio>
      <button 
        className="audio-toggle-btn"
        onClick={handlePlayPause}
        aria-label={isPlaying ? "Pause audio" : "Play audio"}
      >
        <i className={isPlaying ? "fas fa-pause" : "fas fa-play"}></i>
        <span className="audio-label">om namo venkatesaya</span>
      </button>
    </div>
  );
}

export default AudioPlayer;
